import { useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../config/environment';

export default function ManageRSVPPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [rsvps, setRSVPs] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    if (token) {
      loadRSVPs();
    } else {
      setError('Invalid magic link');
      setLoading(false);
    }
  }, [token]);

  const loadRSVPs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/rsvps/manage/${token}`);
      if (!response.ok) throw new Error('Invalid or expired magic link');
      
      const data = await response.json();
      setRSVPs(data.rsvps);
      setEmail(data.email);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (rsvpId) => {
    if (!window.confirm('Are you sure you want to cancel this RSVP?')) return;
    
    setCancelling(rsvpId);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/rsvps/${rsvpId}/cancel`, {
        method: 'PUT',
        headers: { 'X-Magic-Link-Token': token }
      });
      
      if (!response.ok) throw new Error('Failed to cancel RSVP');
      
      const result = await response.json();
      
      // Show success message
      alert(result.message + (result.waitlist_promoted ? ` ${result.promoted_count} person(s) promoted from waitlist.` : ''));
      
      // Reload RSVPs
      await loadRSVPs();
    } catch (err) {
      alert('Failed to cancel RSVP: ' + err.message);
    } finally {
      setCancelling(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#1FA3A3] animate-spin mx-auto mb-4" />
          <p className="text-[#4C5A70]">Loading your RSVPs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-soft border border-[#C7362E] max-w-md">
          <h1 className="font-heading text-2xl font-semibold text-[#0B2240] mb-4">Invalid Link</h1>
          <p className="text-[#4C5A70] mb-6">{error}</p>
          <button
            onClick={() => navigate('/events')}
            className="text-[#1FA3A3] hover:underline"
          >
            ← Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <title>Manage Your RSVPs | KGOB Events</title>
      <meta name="description" content="View and manage your KGOB event registrations" />

      <div className="min-h-screen bg-[#F7F6F2] py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Header */}
            <div className="bg-white rounded-2xl p-8 shadow-soft border border-[#E1DFD7] mb-6">
              <h1 className="font-heading text-3xl font-semibold text-[#0B2240] mb-2">
                Manage Your RSVPs
              </h1>
              <p className="text-[#4C5A70]">
                Registered as: <strong className="text-[#0B2240]">{email}</strong>
              </p>
            </div>

            {/* RSVPs List */}
            {rsvps.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 shadow-soft border border-[#E1DFD7] text-center">
                <p className="text-[#4C5A70]">No upcoming events registered.</p>
                <button
                  onClick={() => navigate('/events')}
                  className="mt-4 text-[#1FA3A3] hover:underline"
                >
                  Browse Events →
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {rsvps.map(rsvp => (
                  <div
                    key={rsvp.id}
                    className="bg-white rounded-2xl p-6 shadow-soft border border-[#E1DFD7]"
                    data-testid="manage-rsvp-card"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-semibold text-[#0B2240] mb-2">
                          {rsvp.event.title}
                        </h3>
                        
                        <div className="space-y-2 text-sm text-[#4C5A70] mb-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {new Date(rsvp.event.start_datetime).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{rsvp.event.location_name}</span>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          {rsvp.status === 'confirmed' && (
                            <span className="px-3 py-1 bg-[#1FA3A3]/10 text-[#1FA3A3] text-xs font-semibold rounded-full">
                              ✓ Confirmed
                            </span>
                          )}
                          {rsvp.checked_in && (
                            <span className="px-3 py-1 bg-[#0B2240] text-white text-xs font-semibold rounded-full">
                              Checked In
                            </span>
                          )}
                          {rsvp.has_guest && (
                            <span className="px-3 py-1 bg-[#EDEBE6] text-[#0B2240] text-xs rounded-full">
                              +1 Guest
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 ml-4">
                        <button
                          onClick={() => navigate(`/events/${rsvp.event.slug}`)}
                          className="px-4 py-2 text-sm text-[#1FA3A3] hover:bg-[#1FA3A3]/10 rounded-lg transition-colors"
                        >
                          View Event
                        </button>
                        
                        {rsvp.can_cancel && (
                          <button
                            onClick={() => handleCancel(rsvp.id)}
                            disabled={cancelling === rsvp.id}
                            className="px-4 py-2 text-sm text-[#C7362E] hover:bg-[#C7362E]/10 rounded-lg transition-colors disabled:opacity-50"
                            data-testid="cancel-rsvp-btn"
                          >
                            {cancelling === rsvp.id ? 'Cancelling...' : 'Cancel RSVP'}
                          </button>
                        )}
                        
                        {!rsvp.can_cancel && rsvp.status === 'confirmed' && (
                          <span className="text-xs text-[#4C5A70] text-center">
                            Past event
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Back to Events */}
            <div className="text-center mt-8">
              <button
                onClick={() => navigate('/events')}
                className="text-[#1FA3A3] hover:underline"
              >
                ← Back to Events
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
